import React, { useEffect, useState } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Avatar,
    Typography,
    Button,
    Chip,
    Spinner,
    Progress
} from "@material-tailwind/react";
import {
    Mail,
    Phone,
    Award,
    Calendar,
    Clock,
    Shield,
    Key,
    FileText,
    Home,
    Users,
    User,
} from "lucide-react";
import moment from 'moment';
import { useAuth } from '../../services/contexts/authContext';
import { convertToPresentableText } from '../../utils/functions';

// Mock function to simulate fetching user data
const useGetUserProfile = (userId) => {
    const { user, userDetails } = useAuth();
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        // Simulate API call
        setUserData({
            _id: userDetails._id,
            name: userDetails.name,
            email: userDetails.email,
            phone: userDetails.phone,
            photoURL: user.photoURL,
            cnic: userDetails.cnic,
            jobTitle: userDetails.jobTitle,
            role: userDetails.role,
            isActive: userDetails.isActive,
            lastLogin: userDetails.lastLogin,
            createdDate: userDetails.createdDate,
            address: userDetails.address,
            meetingRoomQuota: userDetails.meetingRoomQuota,
        });
        // TODO: I need this for the future to add more details to the user profile
        // setTimeout(() => {
        //     setUserData({
        //         _id: "user123",
        //         name: "Sarah Johnson",
        //         email: "sarah.johnson@workbench.pk",
        //         phone: "+92 300 1234567",
        //         photoURL: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&auto=format",
        //         cnic: "61101-1234567-8",
        //         jobTitle: "Senior Product Manager",
        //         department: "Product Development",
        //         role: "manager",
        //         isActive: true,
        //         lastLogin: "2025-05-20T08:30:00Z",
        //         createdDate: "2024-02-15T10:00:00Z",
        //         address: {
        //             street: "123 Business Avenue",
        //             city: "Islamabad",
        //             state: "Federal Territory",
        //             postalCode: "44000",
        //             country: "Pakistan"
        //         },
        //         emergencyContact: {
        //             name: "Michael Johnson",
        //             relationship: "Spouse",
        //             phone: "+92 300 9876543"
        //         },
        //         stats: {
        //             activeContracts: 2,
        //             totalBookingValue: 120000,
        //             occupiedOffices: 1,
        //             occupiedSeats: 3,
        //             leadsAssigned: 15,
        //             leadsConverted: 8
        //         },
        //         bookings: [
        //             {
        //                 id: "booking123",
        //                 type: "Office",
        //                 location: "Tech Park, Floor 3, Office 301",
        //                 startDate: "2025-01-15T00:00:00Z",
        //                 status: "active"
        //             },
        //             {
        //                 id: "booking124",
        //                 type: "Coworking Seat",
        //                 location: "Creative Hub, Floor 2, Seat A12",
        //                 startDate: "2025-03-01T00:00:00Z",
        //                 status: "active"
        //             }
        //         ]
        //     });
        //     setIsLoading(false);
        // }, 1000);
    }, [user, userDetails]);

    return { userData, isLoading: false };
};

// Helper function to format date
// const formatDate = (date) => {
//     if (!date) return 'N/A';
//     return moment(date).format('MMM DD, YYYY');
// };

// Helper function to format date with time
const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('MMM DD, YYYY, h:mm A');
};

const UserProfile = ({ userId = "user123" }) => {
    const { userData, isLoading } = useGetUserProfile(userId);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Spinner className="h-12 w-12" color="blue" />
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="text-center p-8">
                <Typography variant="h5" color="blue-gray">User not found</Typography>
            </div>
        );
    }

    // const getRoleColor = (role) => {
    //     switch (role) {
    //         case 'admin': return 'red';
    //         case 'manager': return 'blue';
    //         case 'sales': return 'green';
    //         default: return 'gray';
    //     }
    // };

    // const getStatusColor = (status) => {
    //     return status === 'active' ? 'green' : 'amber';
    // };

    return (
        <div className='bg-gray-100 min-h-screen'>
            <div className="container mx-auto px-4 py-8 ">
                <Card className="overflow-hidden shadow-none ">
                    <CardHeader
                        color="transparent"
                        className="m-0 rounded-2xl"
                    >
                        <div className="h-40 bg-gradient-to-r from-blue-300 to-blue-700"></div>
                    </CardHeader>

                    <CardBody className="px-6 -mt-16 pb-2">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Profile header with photo */}
                            <div className="flex flex-col items-center md:items-start">
                                {
                                    userData.photoURL ? (
                                        <Avatar
                                            size="xxl"
                                            variant="circular"
                                            src={userData.photoURL}
                                            alt={userData.name}
                                            className="border-4 border-white shadow-lg mb-2"
                                        />
                                    ) : (
                                        <div
                                            className="border-4 border-white shadow-lg rounded-full p-4 z-10 bg-white"
                                        >
                                            <User className="h-10 w-10 text-blue-gray-500" />
                                        </div>
                                    )
                                }
                                <div className="flex flex-col items-center md:items-start mt-2">
                                    <Typography variant="h4" color="blue-gray">
                                        {convertToPresentableText(userData.name)}
                                    </Typography>
                                    <div className="flex items-center gap-2 mt-1">
                                        {/* <Chip
                                        value={userData.role.charAt(0).toUpperCase() + userData.role.slice(1)}
                                        color={getRoleColor(userData.role)}
                                        size="sm"
                                    /> */}
                                        <Chip
                                            value={userData.isActive ? "Active" : "Inactive"}
                                            color={userData.isActive ? "green" : "gray"}
                                            size="sm"
                                        />
                                    </div>
                                    <Typography variant="lead" color="gray" className="mt-1 font-normal">
                                        {userData.jobTitle}
                                    </Typography>
                                </div>
                            </div>

                            {/* Key stats */}
                            {/* <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 lg:mt-0">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <Building className="h-5 w-5 text-blue-500" />
                                    <Typography variant="h6" color="blue-gray">Spaces</Typography>
                                </div>
                                <div className="mt-2">
                                    <Typography className="text-2xl font-bold">
                                        {userData.stats.occupiedOffices + userData.stats.occupiedSeats}
                                    </Typography>
                                    <Typography variant="small" color="gray">
                                        {userData.stats.occupiedOffices} Offices, {userData.stats.occupiedSeats} Seats
                                    </Typography>
                                </div>
                            </div>

                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-green-500" />
                                    <Typography variant="h6" color="blue-gray">Total Value</Typography>
                                </div>
                                <div className="mt-2">
                                    <Typography className="text-2xl font-bold">
                                        {userData.stats.totalBookingValue.toLocaleString()} PKR
                                    </Typography>
                                    <Typography variant="small" color="gray">
                                        {userData.stats.activeContracts} Active Contracts
                                    </Typography>
                                </div>
                            </div>

                            {userData.role === 'sales' && (
                                <div className="bg-amber-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="h-5 w-5 text-amber-500" />
                                        <Typography variant="h6" color="blue-gray">Lead Conversion</Typography>
                                    </div>
                                    <div className="mt-2">
                                        <Typography className="text-2xl font-bold">
                                            {Math.round((userData.stats.leadsConverted / userData.stats.leadsAssigned) * 100)}%
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            {userData.stats.leadsConverted} of {userData.stats.leadsAssigned} Leads
                                        </Typography>
                                        <Progress value={(userData.stats.leadsConverted / userData.stats.leadsAssigned) * 100} color="amber" className="mt-1" />
                                    </div>
                                </div>
                            )}

                            {userData.role !== 'sales' && (
                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Activity className="h-5 w-5 text-purple-500" />
                                        <Typography variant="h6" color="blue-gray">Account Age</Typography>
                                    </div>
                                    <div className="mt-2">
                                        <Typography className="text-2xl font-bold">
                                            {moment().diff(moment(userData.createdDate), 'months')} months
                                        </Typography>
                                        <Typography variant="small" color="gray">
                                            Member since {formatDate(userData.createdDate)}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </div> */}
                        </div>

                        {/* Tabs for different sections */}
                        <div className="mt-8 space-y-4">
                            {/* Contact Information */}
                            <Card className='bg-gray-100 border border-gray-200 shadow-none'>
                                <CardBody>
                                    <Typography variant="h6" color="blue-gray" className="mb-4">
                                        Contact Information
                                    </Typography>

                                    <ul className="space-y-4">
                                        <li className="flex items-start gap-4">
                                            <Mail className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Email Address
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.email}
                                                </Typography>
                                            </div>
                                        </li>

                                        <li className="flex items-start gap-4">
                                            <Phone className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Phone Number
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.phone}
                                                </Typography>
                                            </div>
                                        </li>

                                        <li className="flex items-start gap-4">
                                            <Key className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    CNIC
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.cnic}
                                                </Typography>
                                            </div>
                                        </li>
                                    </ul>
                                </CardBody>
                            </Card>

                            {/* Address Information */}
                            <Card className='bg-gray-100 border border-gray-200 shadow-none'>
                                <CardBody>
                                    <Typography variant="h6" color="blue-gray" className="mb-4">
                                        Meeting Room Bookings Quota
                                    </Typography>
                                    <div className="flex items-start gap-4 col-span-1">
                                        <Home className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                        <div>
                                            <Typography variant="small" color="blue-gray" className="font-semibold">
                                                Total Monthly Hours
                                            </Typography>
                                            <Typography variant="small" color="gray">
                                                {userData.meetingRoomQuota.monthlyHours / 60} hours
                                            </Typography>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">


                                        <div className="flex items-start gap-4">
                                            <Users className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div className='w-full'>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Used Meeting Room Hours
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.meetingRoomQuota.usedHours / 60} hours
                                                </Typography>
                                                <div className="border border-blue-200 rounded-2xl p-2">
                                                    <Progress
                                                        value={100 - (parseInt((userData.meetingRoomQuota.usedHours / userData.meetingRoomQuota.monthlyHours) * 100))}
                                                        color="blue"
                                                        label={`Remaining`}
                                                        className='w-full'
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <Clock className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div className='w-full'>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Remaining Meeting Room Hours
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.meetingRoomQuota.monthlyHours / 60 - userData.meetingRoomQuota.usedHours / 60} hours
                                                </Typography>
                                                <div className="border border-blue-200 rounded-2xl p-2">
                                                    <Progress
                                                        value={parseInt((userData.meetingRoomQuota.usedHours / userData.meetingRoomQuota.monthlyHours) * 100)}
                                                        color="blue"
                                                        label={`Used`}
                                                        className='w-full'
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </CardBody>
                            </Card>

                            <Card className='bg-gray-100 border border-gray-200 shadow-none'>
                                <CardBody>
                                    <Typography variant="h6" color="blue-gray" className="mb-4">
                                        Account Information
                                    </Typography>

                                    <ul className="space-y-4">
                                        <li className="flex items-start gap-4">
                                            <Calendar className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Account Created
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {formatDateTime(userData.createdDate)}
                                                </Typography>
                                            </div>
                                        </li>

                                        <li className="flex items-start gap-4">
                                            <Clock className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Last Login
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {formatDateTime(userData.lastLogin)}
                                                </Typography>
                                            </div>
                                        </li>

                                        <li className="flex items-start gap-4">
                                            <Award className="h-5 w-5 text-blue-gray-500 mt-0.5" />
                                            <div>
                                                <Typography variant="small" color="blue-gray" className="font-semibold">
                                                    Account Status
                                                </Typography>
                                                <Typography variant="small" color="gray">
                                                    {userData.isActive ? 'Active' : 'Inactive'}
                                                </Typography>
                                            </div>
                                        </li>
                                    </ul>
                                </CardBody>
                                {/* <CardFooter className="pt-0">
                                                <Button color="blue" size="sm" className="flex items-center gap-2">
                                                    <Edit className="h-4 w-4" /> Edit Profile
                                                </Button>
                                            </CardFooter> */}
                            </Card>

                            <Card className='bg-gray-100 border border-gray-200 shadow-none'>
                                <CardBody>
                                    <Typography variant="h6" color="blue-gray" className="mb-4">
                                        Security Settings
                                    </Typography>

                                    <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Shield className="h-5 w-5 text-blue-gray-500" />
                                                <Typography variant="small" color="blue-gray">Change Password</Typography>
                                            </div>
                                            <Button variant="text" size="sm" disabled>Update</Button>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Phone className="h-5 w-5 text-blue-gray-500" />
                                                <Typography variant="small" color="blue-gray">Two-Factor Authentication</Typography>
                                            </div>
                                            <Button variant="text" size="sm" disabled>Enable</Button>
                                        </div>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <FileText className="h-5 w-5 text-blue-gray-500" />
                                                <Typography variant="small" color="blue-gray">Login Activity Log</Typography>
                                            </div>
                                            <Button variant="text" size="sm" disabled>View</Button>
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </div>
                        {/* <Card>
                                        <CardBody>
                                            <Typography variant="h6" color="blue-gray" className="mb-4">
                                                Active Bookings
                                            </Typography>

                                            {userData.bookings.length > 0 ? (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full min-w-max table-auto text-left">
                                                        <thead>
                                                            <tr>
                                                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                                                        Type
                                                                    </Typography>
                                                                </th>
                                                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                                                        Location
                                                                    </Typography>
                                                                </th>
                                                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                                                        Start Date
                                                                    </Typography>
                                                                </th>
                                                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                                                        Status
                                                                    </Typography>
                                                                </th>
                                                                <th className="border-b border-blue-gray-100 bg-blue-gray-50 p-4">
                                                                    <Typography variant="small" color="blue-gray" className="font-medium">
                                                                        Actions
                                                                    </Typography>
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {userData.bookings.map((booking, index) => (
                                                                <tr key={booking.id} className={index % 2 === 0 ? '' : 'bg-blue-gray-50/50'}>
                                                                    <td className="p-4">
                                                                        <Typography variant="small" color="blue-gray">
                                                                            {booking.type}
                                                                        </Typography>
                                                                    </td>
                                                                    <td className="p-4">
                                                                        <Typography variant="small" color="blue-gray">
                                                                            {booking.location}
                                                                        </Typography>
                                                                    </td>
                                                                    <td className="p-4">
                                                                        <Typography variant="small" color="blue-gray">
                                                                            {formatDate(booking.startDate)}
                                                                        </Typography>
                                                                    </td>
                                                                    <td className="p-4">
                                                                        <Chip
                                                                            value={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                                                            color={getStatusColor(booking.status)}
                                                                            size="sm"
                                                                        />
                                                                    </td>
                                                                    <td className="p-4">
                                                                        <Button size="sm" variant="text" color="blue">View Details</Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : (
                                                <div className="text-center py-8">
                                                    <Typography color="gray">No active bookings found</Typography>
                                                </div>
                                            )}
                                        </CardBody>
                                    </Card> */}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default UserProfile;